import React, { useState } from 'react'
import {
  Image,
  View,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  StatusBar,
  ActivityIndicator,
  Platform,
  ScrollView
} from 'react-native'
import { useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import { router } from 'expo-router'

import PracticeIcon from '../../../assets/practiceScreen/practiceGameIcon.png'

import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { formatChipsNumber } from '@/components/GameAssets/Player/PlayerBox'
import { Text } from '../../components'
import { MainHeader } from '../../components/MainHeader/MainHeader'
import { WebWrapper } from '@/components/WebWrapper'
import { moderateScale } from '../../styles/sizes'
import { fontFamily } from '../../theme/fontFamily'
import { useStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'

interface GameSetupProps {
  userId: string
  userName: string
}

const BOT_LIST = [2, 3, 4, 5, 6, 7, 8, 9]
const STARTING_CHIPS_LIST = [5000, 10000, 20000, 50000]
// const BIG_BLIND_LIST = [50, 100, 200, 500, 1000]

// Blind increase time options in milliseconds (0 = disabled)
const BLIND_INCREASE_OPTIONS = [
  { label: 'No', value: 0 },
  { label: '4', value: 4 * 60 * 1000 },
  { label: '8', value: 8 * 60 * 1000 },
  { label: '12', value: 12 * 60 * 1000 },
  { label: '15', value: 15 * 60 * 1000 }
]

export function GameSetupScreen({ userId, userName }: GameSetupProps) {
  const insets = useSafeAreaInsets()
  const [gameName, setGameName] = useState('Practice Game')
  const [startingChips, setStartingChips] = useState(10000)
  const [bigBlind, setBigBlind] = useState(100)
  const [botCount, setBotCount] = useState(3)
  const [isCreating, setIsCreating] = useState(false)
  const [blindIncreaseTime, setBlindIncreaseTime] = useState(0) // 0 = disabled
  const {
    analytics: { setEvent },
    session: { user }
  } = useStore()

  const mongoUserId = user?._id.toString()

  const createPracticeGame = useMutation(api.games.mutations.createPracticeGame)
  const joinGame = useMutation(api.games.mutations.joinGame)

  const handleCreateGame = async () => {
    try {
      setIsCreating(true)

      // Create practice game with bots
      const result = await createPracticeGame({
        name: gameName,
        bigBlind: bigBlind,
        botCount: botCount,
        startingChips: startingChips,
        timeBetweenBlindsIncrease: blindIncreaseTime
      })

      if (!result?.gameId) {
        throw new Error('Failed to create game')
      }

      const { gameId } = result

      // Join the game as human player
      await joinGame({
        gameId,
        userId,
        name: userName,
        mongoUserId: mongoUserId
      })

      setEvent(events.game.create_quick_game_tapped, {
        userId: userId,
        isConvexEnabled: true
      })
      // Navigate to poker table
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      router.push({ pathname: '/game/[gameId]', params: { gameId } } as any)
    } catch (error) {
      console.error('Failed to create game:', error)
      alert('Failed to create game. Please try again.')
    } finally {
      setIsCreating(false)
    }
  }

  const onBack = () => {
    router.back()
  }

  const renderBots = ({ item }: { item: number }) => {
    return (
      <TouchableOpacity
        onPress={() => setBotCount(item)}
        style={[styles.botView, { borderColor: item === botCount ? '#121212' : '#12121233' }]}
      >
        <Text style={styles.botText}>{item}</Text>
      </TouchableOpacity>
    )
  }

  // Removed for now. Leaving for future Tournament game creation when needed
  // const renderStartingChips = ({ item }: { item: number }) => {
  //   return (
  //     <TouchableOpacity
  //       onPress={() => setStartingChips(item)}
  //       style={[styles.chipView, { borderColor: item === startingChips ? '#121212' : '#12121233' }]}
  //     >
  //       <Text style={styles.botText}>{formatChipsNumber(item)}</Text>
  //     </TouchableOpacity>
  //   )
  // }

  const renderBigBlinds = ({ item }: { item: number }) => {
    return (
      <TouchableOpacity
        onPress={() => setBigBlind(item)}
        style={[styles.botView, { borderColor: item === bigBlind ? '#121212' : '#12121233' }]}
      >
        <Text style={styles.botText}>{item}</Text>
      </TouchableOpacity>
    )
  }

  const renderBlindIncreaseTime = ({ item }: { item: { label: string; value: number } }) => {
    return (
      <TouchableOpacity
        onPress={() => setBlindIncreaseTime(item.value)}
        style={[styles.chipView, { borderColor: item.value === blindIncreaseTime ? '#121212' : '#12121233' }]}
      >
        <Text style={styles.botText}>{item.label}</Text>
      </TouchableOpacity>
    )
  }

  return (
    <View
      style={[
        styles.container,
        {
          paddingBottom: insets.bottom,
          paddingTop: insets.top
        }
      ]}
    >
      <StatusBar barStyle="dark-content" />
      <WebWrapper>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          bounces={true}
        >
          <MainHeader hideLogo leftPress={onBack} headerContainerStyle={{ marginTop: StatusBar.currentHeight }} />
          <View style={styles.mainContainer}>
            <View style={styles.contentSection}>
              <Image source={PracticeIcon} resizeMode="contain" style={styles.PracticeIcon} />
              <Text tx={'practiceGameScreen.title'} style={styles.title} />
              <Text tx={'practiceGameScreen.description'} style={styles.description} />
              <View style={styles.botContainer}>
                <FlatList
                  data={BOT_LIST}
                  numColumns={4}
                  renderItem={renderBots}
                  keyExtractor={(item) => `bots-${item}`}
                  showsHorizontalScrollIndicator={false}
                  bounces={false}
                  scrollEnabled={false}
                />
              </View>

              {/* Removed for now. Leaving for future Tournament game creation when needed */}
              {/* <View style={styles.botContainer}>
                <Text tx={'practiceGameScreen.startingChips'} style={styles.label} />
                <FlatList
                  horizontal={true}
                  data={STARTING_CHIPS_LIST}
                  renderItem={renderStartingChips}
                  keyExtractor={(item) => `chips-${item}`}
                  showsHorizontalScrollIndicator={false}
                  bounces={false}
                />
              </View> */}

              <View style={styles.botContainer}>
                <Text tx={'practiceGameScreen.blindsIncrease'} style={styles.blindText} />
                <Text tx={'practiceGameScreen.blindSelectionMsg'} style={[styles.blindText, { fontSize: 14 }]} />
                <FlatList
                  horizontal={true}
                  data={BLIND_INCREASE_OPTIONS}
                  renderItem={renderBlindIncreaseTime}
                  keyExtractor={(item) => `blindIncrease-${item.value}`}
                  showsHorizontalScrollIndicator={false}
                  bounces={false}
                />
              </View>

            </View>
            <View style={styles.buttonSection}>
              <TouchableOpacity
                disabled={isCreating || botCount < 1}
                onPress={handleCreateGame}
                style={[styles.buttonStyle, { opacity: botCount < 1 || isCreating ? 0.5 : 1 }]}
              >
                <View style={styles.buttonView}>
                  <Text tx={'practiceGameScreen.play'} style={styles.buttonText} />
                </View>
                {isCreating ? (
                  <View style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <ActivityIndicator size="small" color="#FFFFFF" style={styles.loadingStyle} />
                  </View>
                ) : null}
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      </WebWrapper>
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    backgroundColor: '#E9E238'
  },
  closeIconStyle: {
    tintColor: '#121212'
  },
  scrollView: {
    flex: 1
  },
  scrollContent: {
    flexGrow: 1,
    paddingBottom: 20
  },
  mainContainer: {
    flex: 1,
    backgroundColor: '#E9E238',
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 20,
    paddingTop: 30
  },
  contentSection: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 20
  },
  buttonSection: {
    width: '100%',
    alignItems: 'center',
    paddingTop: 30
  },
  title: {
    color: '#121212',
    fontWeight: '400',
    fontSize: 32,
    fontFamily: fontFamily.FeatureDeckRegular,
    textAlign: 'center',
    marginVertical: 20
  },
  description: {
    color: '#121212',
    fontWeight: '400',
    fontSize: 18,
    fontFamily: fontFamily.GTAmericaStandardRegular,
    textAlign: 'center'
  },
  inputContainer: {
    width: '100%',
    marginTop: 20
  },
  label: {
    color: '#121212',
    fontSize: 16,
    fontFamily: fontFamily.GTAmericaStandardRegular,
    marginBottom: 8,
    textAlign: 'center'
  },
  blindText: {
    color: '#121212',
    fontSize: 16,
    fontFamily: fontFamily.GTAmericaStandardRegular,
    textAlign: 'center'
  },
  textInput: {
    backgroundColor: '#E9E238'
  },
  textInputContent: {
    fontFamily: fontFamily.GTAmericaStandardRegular,
    color: '#121212',
    fontSize: 16
  },
  textInputOutline: {
    borderWidth: 1,
    borderColor: '#121212',
    borderRadius: 8
  },
  botContainer: {
    marginTop: 20,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center'
  },
  botView: {
    height: 40,
    width: 40,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#121212',
    justifyContent: 'center',
    alignItems: 'center',
    margin: 5,
    alignSelf: 'center'
  },
  chipView: {
    height: 40,
    width: 40,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#121212',
    justifyContent: 'center',
    alignItems: 'center',
    margin: 5,
    alignSelf: 'center'
  },
  botText: {
    color: '#121212',
    fontWeight: '700',
    fontSize: 12,
    fontFamily: fontFamily.GTAmericaStandardRegular
  },
  hint: {
    color: '#121212',
    fontSize: 14,
    fontFamily: fontFamily.GTAmericaStandardRegular,
    textAlign: 'center',
    marginTop: 8
  },
  buttonStyle: {
    backgroundColor: '#121212',
    height: moderateScale(48),
    borderRadius: 48,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    position: 'relative',
    width: Platform.OS === 'web' ? 300 : '100%'
  },
  buttonView: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  },
  buttonText: {
    color: '#FFFFFF',
    fontWeight: '500',
    fontSize: 16,
    fontFamily: fontFamily.GTAmericaStandardMedium
  },
  loadingStyle: {
    right: 20,
    position: 'absolute'
  },
  PracticeIcon: {
    height: 134,
    width: 134
  }
})
